import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  CredentialExpiryResult,
  CredentialTargetType,
  CredentialTargetView,
  CredentialsView,
  PutBindingCredentialExpiryBody,
  PutDataflowCredentialExpiryBody,
} from "@shared/types/credentials";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { OVERVIEW_PATH } from "@/hooks/use-overview";
import { CREDENTIALS_PATH, apiRequest } from "@/lib/api";

/**
 * The daily credential check's results (2026-09-28 cred-check design, "UI"):
 * `GET /api/v2/instances/current/credentials`, every target's last check and each field's expiry
 * date and its source. Read once a minute while visible: the runner writes it once a day, and a
 * manual Validate writes it at once (the Validate mutation invalidates this key by prefix).
 *
 * Keyed `[path, instanceId]` like the Overview, so an instance switch never shows the previous
 * instance's rows under the new header.
 */
const CREDENTIALS_POLL_MS = 60_000;

export function useCredentials(enabled = true): UseQueryResult<CredentialsView> {
  const instanceId = useTenantInstance().selectedInstance?.id ?? null;
  return useQuery<CredentialsView>({
    queryKey: [CREDENTIALS_PATH, instanceId],
    enabled: enabled && instanceId !== null,
    queryFn: ({ signal }) => apiRequest<CredentialsView>(CREDENTIALS_PATH, { signal }),
    staleTime: CREDENTIALS_POLL_MS,
    refetchInterval: CREDENTIALS_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/** The one target a screen shows, or null (not checked yet, or no longer a target). */
export function credentialTarget(
  view: CredentialsView | undefined,
  targetType: CredentialTargetType,
  targetId: string,
): CredentialTargetView | null {
  return view?.targets.find((t) => t.targetType === targetType && t.targetId === targetId) ?? null;
}

function useInvalidateCredentials(): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: [CREDENTIALS_PATH] }),
      client.invalidateQueries({ queryKey: [OVERVIEW_PATH] }),
    ]);
  };
}

/** `PUT /dataflows/:id/credential-expiry`. */
export function useSetDataflowExpiry(
  dataflowId: string,
): UseMutationResult<CredentialExpiryResult, Error, PutDataflowCredentialExpiryBody> {
  const invalidate = useInvalidateCredentials();
  return useMutation({
    mutationFn: (body: PutDataflowCredentialExpiryBody) =>
      apiRequest<CredentialExpiryResult>(
        `/api/v2/dataflows/${encodeURIComponent(dataflowId)}/credential-expiry`,
        { method: "PUT", body },
      ),
    onSuccess: invalidate,
  });
}

/** `PUT /instances/current/binding/credential-expiry`. */
export function useSetBindingExpiry(): UseMutationResult<
  CredentialExpiryResult,
  Error,
  PutBindingCredentialExpiryBody
> {
  const invalidate = useInvalidateCredentials();
  return useMutation({
    mutationFn: (body: PutBindingCredentialExpiryBody) =>
      apiRequest<CredentialExpiryResult>("/api/v2/instances/current/binding/credential-expiry", {
        method: "PUT",
        body,
      }),
    onSuccess: invalidate,
  });
}
